import {
  BarRaiserTechDomains,
  ContentUserRole,
  ContentWorkflow,
  getSLADueDate,
  isNearSLADueDate,
  isPastSLADueDate,
  PublisherReviewStatus,
  TechDomain,
  UserProfile,
  WorkflowState,
} from '@amzn/awscat-pattern-library-service-model';
import {
  ContentWorkflowTableItem,
  DefaultContentSelectorOptions,
  DefaultPublishedBarRaiserSelectorOptions,
  getBarRaiserDashboardTableColumns,
  getBarRaiserPublishedDashboardTableColumns,
} from '../workflow-viewer/ContentWorkflowTableDefinitions';
import { WorkflowTableIdentity } from '../../common/CookieRegistry';
import { QueryGroup } from '../content/table/ServerSideQueryContentsProvider';
import { PropertyFilterProps } from '@amzn/awsui-components-core';

export const isBarRaiser = (userProfile: UserProfile): boolean => {
  if (!Array.isArray(userProfile?.contentRoles)) {
    return false;
  }
  const barRaiserRoleConfigs = userProfile.contentRoles.filter(
    (r) => r.contentRole === ContentUserRole.BarRaiser,
  );
  return barRaiserRoleConfigs.some(
    (c) =>
      // @ts-expect-error TS2345 - strict migration
      ['mainTechDomain', 'techDomains'].includes(c.filter?.metadataAttributeName) &&
      // @ts-expect-error TS2345 - strict migration
      BarRaiserTechDomains.includes(c.filter?.metadataAttributeValue),
  );
};

export const getDashboardOverview = (wfItems: ContentWorkflowTableItem[]) => {
  // Pre-authoring review is deprecated: items are never counted, but the field is
  // retained at 0 because the external BarRaiserDashboardInfo type still requires it.
  const preauthorCount = 0;
  let publisherCount = 0;
  let nearSLACount = 0;
  let pastSLACount = 0;

  wfItems.forEach((item) => {
    const { draftState } = item.workflow;
    if (draftState === WorkflowState.REVIEWED || draftState === WorkflowState.PUBLISHER_REVIEW) {
      publisherCount++;
    } else {
      return;
    }

    const { isNearDue, isPastDue } = getContentSLAs(item.workflow);
    if (isNearDue) {
      nearSLACount++;
    } else if (isPastDue) {
      pastSLACount++;
    }
  });

  return {
    preauthorCount,
    publisherCount,
    nearSLACount,
    pastSLACount,
    totalCount: publisherCount,
  };
};

// A PUBLISHER_REVIEW workflow whose review outcome is anything other than pending; the bar raiser
// queue shows only pending publisher reviews, so these are filtered out. Workflows in other states
// (e.g. REVIEWED) are never matched and stay visible.
export const isNonPendingPublisherReview = (wf: ContentWorkflow): boolean =>
  wf.draftState === WorkflowState.PUBLISHER_REVIEW &&
  wf.publisherReviewInfo?.reviewStatus !== PublisherReviewStatus.PENDING;

export const getContentSLAs = (wf: ContentWorkflow) => {
  const dueDate = getSLADueDate(wf);
  const isNearDue = isNearSLADueDate(dueDate);
  const isPastDue = isPastSLADueDate(dueDate);

  return { isNearDue, isPastDue };
};

export const draftTableColumns = getBarRaiserDashboardTableColumns({
  tableId: WorkflowTableIdentity.DraftBarRaiserDashboard,
});
export const publishedTableColumns = getBarRaiserPublishedDashboardTableColumns({
  tableId: WorkflowTableIdentity.PublishedBarRaiserDashboard,
});

export const allowedStatesMap = new Map<WorkflowTableIdentity, WorkflowState[]>([
  [
    WorkflowTableIdentity.DraftBarRaiserDashboard,
    [WorkflowState.REVIEWED, WorkflowState.PUBLISHER_REVIEW],
  ],
  [WorkflowTableIdentity.PublishedBarRaiserDashboard, [WorkflowState.UNMODIFIED]],
]);

export const queryGroups = (
  techDomain: TechDomain,
  workflowStates?: WorkflowState[],
): QueryGroup[] => {
  const states =
    workflowStates?.map((state) => ({
      propertyKey: 'workflowState',
      value: state,
      operator: '=',
    })) || [];

  return [
    {
      id: '1',
      query: {
        tokens: [
          {
            value: techDomain,
            propertyKey: 'mainTechDomain',
            operator: '=',
          },
          ...states,
        ],
        operation: 'and',
      },
    },
  ];
};

export const defaultQueryGroupsOperation: PropertyFilterProps.JoinOperation = 'and';

export const draftDashboardQueryGroups = (techDomain: TechDomain) =>
  queryGroups(techDomain, allowedStatesMap.get(WorkflowTableIdentity.DraftBarRaiserDashboard));
export const publishedDashboardQueryGroups = (techDomain: TechDomain) =>
  queryGroups(techDomain, allowedStatesMap.get(WorkflowTableIdentity.PublishedBarRaiserDashboard));

export const draftContentSelectorOptions = DefaultContentSelectorOptions.filter((o) =>
  draftTableColumns.some((c) => c.id === o.id),
);
export const publishedContentSelectorOptions = DefaultPublishedBarRaiserSelectorOptions.filter(
  (o) => publishedTableColumns.some((c) => c.id === o.id),
);
